/**
 * Light or dark, chosen on this device (NFR-UX-5).
 *
 * The fit criterion asks that the system color preference be "honored by default
 * and overridable". The stylesheet has always done the first half with a
 * `prefers-color-scheme` query; this is the second. The override is one attribute
 * on the document root -- `data-theme` -- which the stylesheet reads, so there is
 * a single palette with two switches in front of it rather than two palettes to
 * keep in step.
 *
 * `system` is a value, not the absence of one. Someone whose laptop turns dark at
 * sunset and who wants the app to follow has said something different from
 * someone who has never been asked, and only a named third state can tell those
 * apart -- it is also the only thing that lets a choice be undone, which a plain
 * light/dark switch cannot offer.
 *
 * The preference lives in `localStorage` rather than in the event log. It is a
 * fact about a device and the light it is being used in, not a fact about the
 * manifest: a phone in bed and a desk at noon want different answers, and syncing
 * one to the other would be a bug wearing the clothes of a feature. Losing it
 * costs a preference and no data, which is the same bargain `clock.ts` makes for
 * the device id.
 */
export type ThemePreference = 'system' | 'light' | 'dark';

/** What is actually on screen, once the system preference has been folded in. */
export type Theme = 'light' | 'dark';

/**
 * The choices, in the order they are offered.
 *
 * `system` comes first because it is the default and the one most people should
 * leave alone, not because it is the safest-sounding.
 */
export const THEMES: readonly { value: ThemePreference; label: string }[] = [
  { value: 'system', label: 'Match my system' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

const KEY = 'intentionalist.theme';
const DARK = '(prefers-color-scheme: dark)';

const isPreference = (value: string | null): value is ThemePreference =>
  value === 'system' || value === 'light' || value === 'dark';

/**
 * What this device has chosen, or `system` if it has not chosen.
 *
 * An unreadable or unrecognized value falls back rather than throwing. It can
 * come from storage the browser has denied, from private browsing, or from a
 * build that spelled the values differently, and none of those deserves a blank
 * page over a color.
 */
export function storedTheme(): ThemePreference {
  try {
    const stored = localStorage.getItem(KEY);
    return isPreference(stored) ? stored : 'system';
  } catch {
    return 'system';
  }
}

/** Whether the system is asking for dark, as far as this browser will say. */
function systemPrefersDark(): boolean {
  try {
    return matchMedia(DARK).matches;
  } catch {
    // No `matchMedia` to ask. Light is the stylesheet's own default, so saying
    // light here agrees with what will actually be painted.
    return false;
  }
}

/** The theme a preference resolves to right now. */
export const resolveTheme = (preference: ThemePreference): Theme =>
  preference === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : preference;

/**
 * Puts the choice on the document, where the stylesheet can see it.
 *
 * `system` removes the attribute rather than setting it to `system`: absence is
 * what the media query is written against, so CSS needs two selectors instead of
 * three and an unknown value can only ever mean "follow the system".
 *
 * The browser chrome is told as well. `theme-color` is a single value in the HTML
 * and so was necessarily wrong for one of the two themes -- a dark address bar
 * above a light page. It now follows whatever `--bg` resolved to, read back from
 * the stylesheet rather than repeated here, because a palette written down in two
 * languages is a palette that drifts.
 */
export function applyTheme(preference: ThemePreference): void {
  const root = document.documentElement;
  if (preference === 'system') root.removeAttribute('data-theme');
  else root.dataset.theme = preference;

  const background = getComputedStyle(root).getPropertyValue('--bg').trim();
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta !== null && background !== '') meta.setAttribute('content', background);
}

/** Records the choice on this device, and applies it. */
export function setTheme(preference: ThemePreference): void {
  try {
    localStorage.setItem(KEY, preference);
  } catch {
    // Storage denied. The choice still holds for this session; it is forgotten on
    // reload, which is the same way the device id degrades.
  }
  applyTheme(preference);
}

/**
 * Applies the stored choice, and reports what it was.
 *
 * Called from `main.tsx` before the first render rather than from a component,
 * because a theme applied during rendering is a theme the user watches change.
 */
export function startTheme(): ThemePreference {
  const preference = storedTheme();
  applyTheme(preference);
  return preference;
}

/**
 * Calls back when the system preference changes, and returns an unsubscribe.
 *
 * Needed even though CSS follows the query on its own: `theme-color` is set in
 * script, and the settings screen says which theme `system` currently resolves
 * to. Both would go stale at sunset without this.
 */
export function watchSystemTheme(onChange: () => void): () => void {
  try {
    const query = matchMedia(DARK);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  } catch {
    // A browser that cannot report the change still renders both themes; the
    // readout is what goes stale, and only until the next load.
    return () => undefined;
  }
}

/** Testing seam only. */
export function resetTheme(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // Nothing depends on this having worked.
  }
  document.documentElement.removeAttribute('data-theme');
}
